import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import {
  createLink,
  fetchFrise,
  fetchLinks,
  LIMITS,
  queryKeys,
  revokeLink,
  type ShareLink,
  shareUrl,
  type Track,
  updateLink,
} from "./api";

/**
 * « Liens à envoyer » (page Proches ; #588) : le proprietaire cree un lien, choisit les pistes que
 * la personne verra, et envoie l'adresse. Le lien ouvre ces pistes en lecture seule a quiconque
 * l'a, sans compte ; les moments prives ne sont jamais montres.
 *
 * **Le jeton ne s'affiche qu'une fois**, juste apres la creation, et vit dans l'etat de ce
 * composant seulement : ni dans le cache des requetes, ni dans le stockage du navigateur. La
 * liste ne le rend jamais (le serveur n'en garde que le SHA-256).
 */

const FIELD =
  "min-h-11 w-full rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground placeholder:text-[#8C96AA]";
const BUTTON = "min-h-11 rounded-[10px] border border-input px-3 text-[13px] disabled:opacity-60";

const asMessage = (error: unknown): string =>
  error instanceof ApiError ? error.message : "La demande n'a pas abouti. Réessaie.";

/** `2026-10-23T10:00:00.000Z` devient `23/10/2026` (le jour UTC, sans fuseau du navigateur). */
export const dayOf = (iso: string): string =>
  `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

/** « montre 0 point », « montre 1 point », « montre 12 points » : ce que le lien ouvre en ce moment. */
export const showsLabel = (count: number): string =>
  `montre ${String(count)} ${count <= 1 ? "point" : "points"}`;

/** « Jamais ouvert », « 1 vue, le 23/10/2026 », « 3 vues, la dernière le 23/10/2026 ». */
export function viewsLabel(link: Pick<ShareLink, "viewCount" | "lastViewedAt">): string {
  if (link.viewCount === 0 || link.lastViewedAt === null) return "jamais ouvert";
  return link.viewCount === 1
    ? `1 vue, le ${dayOf(link.lastViewedAt)}`
    : `${String(link.viewCount)} vues, la dernière le ${dayOf(link.lastViewedAt)}`;
}

/** Les cases des pistes : un groupe nomme, la couleur ne portant jamais seule le sens. */
function TrackChecks({
  legend,
  tracks,
  chosen,
  onToggle,
}: {
  legend: string;
  tracks: readonly Track[];
  chosen: ReadonlySet<number>;
  onToggle: (id: number) => void;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-0.5 border-0 p-0">
      <legend className="mb-1 text-[13px] text-[#C9D0DE]">{legend}</legend>
      {tracks.map((track) => (
        <label className="flex min-h-11 items-center gap-2.5 text-[15px]" key={track.id}>
          <input
            checked={chosen.has(track.id)}
            className="size-5 accent-primary"
            onChange={() => onToggle(track.id)}
            type="checkbox"
          />
          <span
            aria-hidden="true"
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: track.color }}
          />
          {track.name}
        </label>
      ))}
    </fieldset>
  );
}

const toggled = (set: ReadonlySet<number>, id: number): Set<number> => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

export function ShareLinks() {
  const queryClient = useQueryClient();
  const headingId = useId();
  const links = useQuery({ queryKey: queryKeys.links, queryFn: fetchLinks, retry: false });
  const frise = useQuery({ queryKey: queryKeys.frise, queryFn: fetchFrise, retry: false });
  const tracks = frise.data?.tracks ?? [];
  /** Le lien qui vient d'etre cree, avec son jeton : montre une fois, puis oublie. */
  const [fresh, setFresh] = useState<{ label: string; url: string; entryCount: number } | null>(
    null,
  );
  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.links });

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2.5">
      <h2 className="m-0 text-[15px] font-semibold" id={headingId}>
        Liens à envoyer
      </h2>
      <p className="m-0 text-xs text-muted-foreground">
        Un lien ouvre en lecture seule les pistes que tu choisis, sans compte, à quiconque l'a. Le
        lien montre les points visibles par la famille sur ces pistes. Les points privés et ceux
        réservés à des personnes précises n'y sont jamais. Tu peux révoquer un lien à tout moment.
      </p>
      {fresh !== null ? <FreshLink fresh={fresh} onDone={() => setFresh(null)} /> : null}
      <CreateLinkForm
        onCreated={(created) => {
          setFresh(created);
          refresh();
        }}
        tracks={tracks}
      />
      {links.isError ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          Tes liens ne répondent pas pour le moment.
        </p>
      ) : null}
      {links.isSuccess && links.data.links.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">Aucun lien pour le moment.</p>
      ) : null}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {(links.data?.links ?? []).map((link) => (
          <LinkItem key={link.id} link={link} onChanged={refresh} tracks={tracks} />
        ))}
      </ul>
    </section>
  );
}

function CreateLinkForm({
  tracks,
  onCreated,
}: {
  tracks: readonly Track[];
  onCreated: (created: { label: string; url: string; entryCount: number }) => void;
}) {
  const id = useId();
  const [label, setLabel] = useState("");
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => createLink(label.trim(), [...chosen]),
    onSuccess: (created) => {
      onCreated({
        label: created.link.label,
        url: shareUrl(created.token),
        entryCount: created.link.entryCount,
      });
      setLabel("");
      setChosen(new Set());
    },
    onError: (thrown) => setError(asMessage(thrown)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (label.trim() === "") return setError("Dis pour qui est ce lien.");
    if (chosen.size === 0) return setError("Choisis au moins une piste.");
    create.mutate();
  };

  return (
    <form
      aria-labelledby={`${id}-titre`}
      className="flex flex-col gap-2.5 rounded-xl border border-border bg-card p-3.5"
      noValidate
      onSubmit={submit}
    >
      <h3 className="m-0 text-sm font-semibold" id={`${id}-titre`}>
        Créer un lien
      </h3>
      <label className="text-[13px] text-[#C9D0DE]" htmlFor={`${id}-pour`}>
        Pour qui ? (une note pour toi, la personne ne la voit pas)
      </label>
      <input
        autoComplete="off"
        className={FIELD}
        id={`${id}-pour`}
        maxLength={LIMITS.shareLabel}
        onChange={(event) => setLabel(event.target.value)}
        placeholder="Tata Marie"
        value={label}
      />
      <TrackChecks
        chosen={chosen}
        legend="Pistes ouvertes par ce lien"
        onToggle={(trackId) => setChosen(toggled(chosen, trackId))}
        tracks={tracks}
      />
      <button
        className="min-h-11 self-start rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        disabled={create.isPending}
        type="submit"
      >
        Créer le lien
      </button>
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** L'adresse du lien neuf, montree une fois, avec « Copier » et l'avertissement. */
function FreshLink({
  fresh,
  onDone,
}: {
  fresh: { label: string; url: string; entryCount: number };
  onDone: () => void;
}) {
  const id = useId();
  const field = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fresh.url);
      setCopied("yes");
    } catch {
      // Pas de presse-papiers (page non securisee, refus) : on selectionne, il reste Ctrl+C.
      field.current?.select();
      setCopied("no");
    }
  };

  return (
    <section
      aria-labelledby={`${id}-titre`}
      className="flex flex-col gap-2 rounded-xl border border-primary bg-card p-3.5"
    >
      <h3 className="m-0 text-sm font-semibold" id={`${id}-titre`}>
        Ton lien pour {fresh.label}
      </h3>
      <p className="m-0 text-sm text-[#C9D0DE]" role="status">
        Garde-le : il ne sera plus affiché. Quiconque a ce lien voit ces pistes.
      </p>
      <p className="m-0 text-sm text-[#C9D0DE]">
        Ce lien {showsLabel(fresh.entryCount)}.
        {fresh.entryCount === 0
          ? " Il est vide pour l'instant : tes points sont privés tant que tu ne les ouvres pas à la famille."
          : ""}
      </p>
      <label className="sr-only" htmlFor={`${id}-url`}>
        Adresse du lien
      </label>
      <input
        className={FIELD}
        id={`${id}-url`}
        onFocus={(event) => event.currentTarget.select()}
        readOnly
        ref={field}
        value={fresh.url}
      />
      <div className="flex flex-wrap gap-2">
        <button
          className="min-h-11 rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground"
          onClick={() => void copy()}
          type="button"
        >
          Copier
        </button>
        <button className={BUTTON} onClick={onDone} type="button">
          C'est copié, fermer
        </button>
      </div>
      <div aria-live="polite">
        {copied === "yes" ? <p className="m-0 text-sm text-tl-ok">Lien copié.</p> : null}
        {copied === "no" ? (
          <p className="m-0 text-sm text-muted-foreground">
            Copie impossible ici : l'adresse est sélectionnée, copie-la à la main.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function LinkItem({
  link,
  tracks,
  onChanged,
}: {
  link: ShareLink;
  tracks: readonly Track[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set(link.trackIds));
  const [error, setError] = useState<string | null>(null);
  const revoked = link.revokedAt !== null;

  const save = useMutation({
    mutationFn: () => updateLink(link.id, { trackIds: [...chosen] }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
    onError: (thrown) => setError(asMessage(thrown)),
  });
  const revoke = useMutation({
    mutationFn: () => revokeLink(link.id),
    onSuccess: () => {
      setConfirming(false);
      onChanged();
    },
    onError: (thrown) => setError(asMessage(thrown)),
  });

  const names = link.trackIds.map(
    (trackId) => tracks.find((track) => track.id === trackId)?.name ?? "piste masquée",
  );

  return (
    <li
      className={`flex flex-col gap-2 rounded-xl border border-border bg-card p-3 ${revoked ? "opacity-70" : ""}`}
    >
      <div className="flex flex-col gap-0.5">
        <strong className="text-[15px]">
          {link.label}
          {revoked ? (
            <span className="ml-2 text-xs font-normal text-destructive">Révoqué</span>
          ) : null}
        </strong>
        <span className="text-xs text-muted-foreground">
          {names.length === 0 ? "Aucune piste" : names.join(", ")}
        </span>
        <span className="text-xs text-muted-foreground">
          Créé le {dayOf(link.createdAt)}
          {revoked ? "" : ` · ${showsLabel(link.entryCount)}`} · {viewsLabel(link)}
          {revoked ? ` · révoqué le ${dayOf(link.revokedAt ?? link.createdAt)}` : ""}
        </span>
      </div>
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {editing ? (
        <div className="flex flex-col gap-2">
          <TrackChecks
            chosen={chosen}
            legend={`Pistes ouvertes pour ${link.label}`}
            onToggle={(trackId) => setChosen(toggled(chosen, trackId))}
            tracks={tracks}
          />
          <div className="flex gap-2">
            <button
              className="min-h-11 rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              disabled={save.isPending || chosen.size === 0}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              type="button"
            >
              Enregistrer les pistes
            </button>
            <button
              className={BUTTON}
              onClick={() => {
                setEditing(false);
                setChosen(new Set(link.trackIds));
              }}
              type="button"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : null}
      {!revoked && !editing ? (
        <div className="flex flex-wrap gap-2">
          <button
            aria-label={`Modifier les pistes de ${link.label}`}
            className={BUTTON}
            onClick={() => {
              setChosen(new Set(link.trackIds));
              setEditing(true);
            }}
            type="button"
          >
            Modifier les pistes
          </button>
          {confirming ? (
            <span className="flex gap-2">
              <button
                className="min-h-11 rounded-lg border border-destructive px-3 text-[13px] text-destructive disabled:opacity-60"
                disabled={revoke.isPending}
                onClick={() => {
                  setError(null);
                  revoke.mutate();
                }}
                type="button"
              >
                Oui, révoquer
              </button>
              <button className={BUTTON} onClick={() => setConfirming(false)} type="button">
                Annuler
              </button>
            </span>
          ) : (
            <button
              aria-label={`Révoquer le lien de ${link.label}`}
              className={BUTTON}
              onClick={() => setConfirming(true)}
              type="button"
            >
              Révoquer
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}
